import React, { useState } from 'react';
import {
  signInWithRedirect,
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
} from 'aws-amplify/auth';
import koneLogo from '../assets/kone-logo.png';

const LoginSelection = ({ onAuthSuccess }) => {
  const [loginMode, setLoginMode] = useState('selection'); // 'selection', 'cognito-signin', 'cognito-signup', 'cognito-confirm'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    firstName: '',
    lastName: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSAMLLogin = async () => {
    try {
      setLoading(true);
      await signInWithRedirect({
        provider: { custom: 'KONE-AzureAD' },
      });
    } catch (error) {
      console.error('SAML login failed:', error);
      setError('SAML login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleCognitoSignIn = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const result = await signIn({
        username: formData.username,
        password: formData.password,
      });

      if (result.isSignedIn) {
        onAuthSuccess();
      }
    } catch (error) {
      console.error('Cognito sign in failed:', error);
      setError(error.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleCognitoSignUp = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = await signUp({
        username: formData.username,
        password: formData.password,
        attributes: {
          email: formData.email,
          given_name: formData.firstName,
          family_name: formData.lastName,
        },
      });

      if (result.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setLoginMode('cognito-confirm');
        setMessage('Please check your email for the confirmation code.');
      }
    } catch (error) {
      console.error('Cognito sign up failed:', error);
      setError(error.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      await confirmSignUp({
        username: formData.username,
        confirmationCode: formData.confirmationCode,
      });

      setMessage('Account confirmed successfully! You can now sign in.');
      setLoginMode('cognito-signin');
    } catch (error) {
      console.error('Confirmation failed:', error);
      setError(error.message || 'Confirmation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      await resendSignUpCode({ username: formData.username });
      setMessage('Confirmation code resent to your email.');
    } catch (error) {
      console.error('Resend code failed:', error);
      setError(error.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  if (loginMode === 'selection') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="text-center mb-4">
            <img src={koneLogo} alt="KONE" style={{ height: '48px', marginBottom: '16px' }} />
            <h2 style={{ color: '#111827', fontWeight: 700, fontSize: '1.5rem' }} className="mb-1">
              Insight Miner
            </h2>
            <p style={{ color: '#6B7280', fontSize: '14px' }} className="mb-0">
              Sign in to get started
            </p>
          </div>

          <div className="d-grid gap-3 mt-4">
            <button
              className="kone-btn kone-btn-primary w-100"
              onClick={handleSAMLLogin}
              disabled={loading}
              style={{ height: '48px', fontSize: '15px', borderRadius: '8px' }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Redirecting...
                </>
              ) : (
                <>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 21 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: '8px' }}
                  >
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                  Sign in with KONE Azure AD
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loginMode === 'cognito-signin') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="text-center mb-4">
            <img src={koneLogo} alt="KONE" style={{ height: '48px', marginBottom: '16px' }} />
            <h2 style={{ color: '#111827', fontWeight: 700, fontSize: '1.5rem' }} className="mb-1">
              Sign In
            </h2>
            <p style={{ color: '#6B7280', fontSize: '14px' }} className="mb-0">
              Enter your Cognito credentials
            </p>
          </div>

          <form onSubmit={handleCognitoSignIn}>
            <div className="kone-form-group">
              <label className="kone-label">Username</label>
              <input
                type="text"
                className="kone-input"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="kone-form-group">
              <label className="kone-label">Password</label>
              <input
                type="password"
                className="kone-input"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="d-grid gap-2 mt-4">
              <button
                type="submit"
                className="kone-btn kone-btn-primary"
                disabled={loading}
                style={{ height: '44px', borderRadius: '8px' }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
              <button
                type="button"
                className="kone-btn kone-btn-secondary"
                onClick={() => setLoginMode('selection')}
                disabled={loading}
                style={{ borderRadius: '8px' }}
              >
                Back
              </button>
            </div>
          </form>

          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div className="alert alert-success mt-3" role="alert">
              {message}
            </div>
          )}

          <div className="text-center mt-3">
            <small style={{ color: '#6B7280' }}>
              Don't have an account?{' '}
              <button
                className="kone-btn"
                style={{
                  padding: '0',
                  background: 'none',
                  border: 'none',
                  color: '#1C64F2',
                  fontWeight: 500,
                }}
                onClick={() => setLoginMode('cognito-signup')}
              >
                Sign up
              </button>
            </small>
          </div>
        </div>
      </div>
    );
  }

  if (loginMode === 'cognito-signup') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="text-center mb-4">
            <img src={koneLogo} alt="KONE" style={{ height: '48px', marginBottom: '16px' }} />
            <h2 style={{ color: '#111827', fontWeight: 700, fontSize: '1.5rem' }} className="mb-1">
              Create Account
            </h2>
            <p style={{ color: '#6B7280', fontSize: '14px' }} className="mb-0">
              Sign up for a new Cognito account
            </p>
          </div>

          <form onSubmit={handleCognitoSignUp}>
            <div className="row">
              <div className="col-md-6 kone-form-group">
                <label className="kone-label">First Name</label>
                <input
                  type="text"
                  className="kone-input"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-6 kone-form-group">
                <label className="kone-label">Last Name</label>
                <input
                  type="text"
                  className="kone-input"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="kone-form-group">
              <label className="kone-label">Username</label>
              <input
                type="text"
                className="kone-input"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="kone-form-group">
              <label className="kone-label">Email</label>
              <input
                type="email"
                className="kone-input"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="kone-form-group">
              <label className="kone-label">Password</label>
              <input
                type="password"
                className="kone-input"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
              <small className="text-muted">
                Min 8 chars with uppercase, lowercase, number and symbol
              </small>
            </div>
            <div className="kone-form-group">
              <label className="kone-label">Confirm Password</label>
              <input
                type="password"
                className="kone-input"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="d-grid gap-2 mt-4">
              <button
                type="submit"
                className="kone-btn kone-btn-primary"
                disabled={loading}
                style={{ height: '44px', borderRadius: '8px' }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
              <button
                type="button"
                className="kone-btn kone-btn-secondary"
                onClick={() => setLoginMode('selection')}
                disabled={loading}
                style={{ borderRadius: '8px' }}
              >
                Back
              </button>
            </div>
          </form>

          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}

          <div className="text-center mt-3">
            <small style={{ color: '#6B7280' }}>
              Already have an account?{' '}
              <button
                className="kone-btn"
                style={{
                  padding: '0',
                  background: 'none',
                  border: 'none',
                  color: '#1C64F2',
                  fontWeight: 500,
                }}
                onClick={() => setLoginMode('cognito-signin')}
              >
                Sign in
              </button>
            </small>
          </div>
        </div>
      </div>
    );
  }

  if (loginMode === 'cognito-confirm') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="text-center mb-4">
            <img src={koneLogo} alt="KONE" style={{ height: '48px', marginBottom: '16px' }} />
            <h2 style={{ color: '#111827', fontWeight: 700, fontSize: '1.5rem' }} className="mb-1">
              Confirm Account
            </h2>
            <p style={{ color: '#6B7280', fontSize: '14px' }} className="mb-0">
              Enter the confirmation code sent to your email
            </p>
          </div>

          <form onSubmit={handleConfirmSignUp}>
            <div className="kone-form-group">
              <label className="kone-label">Username</label>
              <input type="text" className="kone-input" value={formData.username} disabled />
            </div>
            <div className="kone-form-group">
              <label className="kone-label">Confirmation Code</label>
              <input
                type="text"
                className="kone-input"
                name="confirmationCode"
                value={formData.confirmationCode}
                onChange={handleInputChange}
                required
                placeholder="Enter 6-digit code"
              />
            </div>
            <div className="d-grid gap-2 mt-4">
              <button
                type="submit"
                className="kone-btn kone-btn-primary"
                disabled={loading}
                style={{ height: '44px', borderRadius: '8px' }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Confirming...
                  </>
                ) : (
                  'Confirm Account'
                )}
              </button>
              <button
                type="button"
                className="kone-btn kone-btn-secondary"
                onClick={handleResendCode}
                disabled={loading}
                style={{ borderRadius: '8px' }}
              >
                Resend Code
              </button>
              <button
                type="button"
                className="kone-btn kone-btn-secondary"
                onClick={() => setLoginMode('selection')}
                disabled={loading}
                style={{ borderRadius: '8px' }}
              >
                Back to Login
              </button>
            </div>
          </form>

          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div className="alert alert-success mt-3" role="alert">
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default LoginSelection;
